import { useOnceAnyDay } from '@flowus/common/hooks/use-once-any-day';
import { IpcMessage, OnceAnyDayIds } from '@flowus/shared';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { isWindows } from 'react-device-detect';
import type { IconName } from 'src/common/components/icon';
import { Icon } from 'src/common/components/icon';
import { message } from 'src/common/components/message';
import { NextMenuItem } from 'src/common/components/next-menu-item';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tooltip } from 'src/common/components/tooltip';
import { publicLink } from 'src/common/const';
import { useModel } from 'src/common/create-model';
import { useHasNewActivity } from 'src/hooks/activities/use-has-new-activity';
import { getLargeVersion, getWindowsYml, useAppVersion } from 'src/hooks/public/use-version';
import { useIsGuest } from 'src/hooks/share/use-permission-utils';
import { useGetSpaceRolePermission } from 'src/hooks/space/use-get-space-role-permission';
import { useClearUser, useCurrentUser, useLogout } from 'src/hooks/user';
import { useExChangeCodeRedBadge } from 'src/services/app/hook';
import { useIsUpdateAvailable, useIsUpdateReload } from 'src/services/desktop/hook';
import { bizTracker } from 'src/utils/biz-tracker';
import { ipcSendMessage } from 'src/utils/electron-util';
import { SettingModalCommon, SettingProvider } from './common';
import { SettingMenuType } from './type';
import { useCloseSettingModal } from './use-open-setting-modal';
import { ProIcon } from '../header/pro_icon';

export const SettingAside: FC = memo(() => {
  const openModal = useOpenModal();
  const closeSettingModal = useCloseSettingModal();
  const { currentSettingMenu, setCurrentSettingMenu } = useModel(SettingProvider);
  const currentUser = useCurrentUser();
  const getSpaceRolePermission = useGetSpaceRolePermission();
  const { editor } = getSpaceRolePermission(currentUser.uuid);
  const logout = useLogout();
  const isGuest = useIsGuest();
  const hasNewActivity = useHasNewActivity();
  const exChangeCodeRedBadge = useExChangeCodeRedBadge();
  const { showOnce: profitShowPoint, close: closeProfitShowPoint } = useOnceAnyDay(
    OnceAnyDayIds.SettingProfitRedPoint,
    {
      endDay: -1,
      enable: !__PRIVATE__,
    }
  );

  const switchType = (type: SettingMenuType) => {
    if (type === SettingMenuType.profit) {
      closeProfitShowPoint();
    }
    setCurrentSettingMenu(type);
  };

  const menuList = useMemo(() => {
    const list = [
      {
        type: SettingMenuType.line,
        name: '账号',
      },
      {
        type: SettingMenuType.account,
        icon: 'IcAccountMiddle',
        name: '账号设置',
      },
      {
        type: SettingMenuType.general,
        icon: 'IcGeneralSettingMiddle',
        name: '通用设置',
      },
      {
        type: SettingMenuType.integral,
        icon: 'IcGiftMiddle',
        name: '活动中心',
        hidden: __BUILD_IN__ || __PRIVATE__,
        badge:
          hasNewActivity || exChangeCodeRedBadge ? (
            <span className="flex items-center absolute right-2.5 top-[calc(50%-5px)] text-t4-bold w-2.5 h-2.5 bg-red rounded-full" />
          ) : undefined,
      },
      {
        type: SettingMenuType.profit,
        icon: 'IcCoinMiddle',
        name: '邀请返佣',
        hidden: __BUILD_IN__ || __PRIVATE__,
        badge: profitShowPoint ? (
          <ProIcon text="💰赚佣金" absolute={false} className="!rounded pointer-events-none" />
        ) : undefined,
      },
      {
        name: '空间',
        type: SettingMenuType.line,
      },
    ];

    if (editor) {
      list.push({
        icon: 'IcSpaceMiddle',
        type: SettingMenuType.spaceInfo,
        name: '空间信息',
      });
    }

    list.push({
      icon: 'IcSettingsMiddle',
      type: SettingMenuType.space,
      name: '空间设置',
    });

    if (!isGuest) {
      list.push({
        icon: 'IcMemberMiddle',
        type: SettingMenuType.members,
        name: '空间成员',
      });
      list.push({
        icon: 'IcGlobeShareMiddle',
        type: SettingMenuType.sharePage,
        name: '公开分享',
      });
      list.push({
        badge: undefined,
        icon: 'IcUpgradeMiddle',
        type: SettingMenuType.upgrade,
        name: '升级',
      });
      if (editor) {
        list.push({
          hidden: __PRIVATE__,
          badge: undefined,
          icon: 'IcShoppingCartMiddle',
          type: SettingMenuType.order,
          name: '订单',
        });
      }
    }
    return list;
  }, [editor, exChangeCodeRedBadge, hasNewActivity, isGuest, profitShowPoint]);

  return (
    <div className="flex flex-col shrink-0 px-2 pt-3 w-[20%] min-w-[200px] text-grey3 border-r border-black_006 text-t2 overflow-y-auto">
      <div className="flex-1">
        {menuList.map((item, i) => {
          if (item.hidden) return null;

          if (item.type === SettingMenuType.line) {
            return (
              <span key={i} className="flex items-center px-2 h-9 text-t2">
                {item.name}
              </span>
            );
          }

          return (
            <NextMenuItem
              dataTestId={item.name}
              activeStyle="bg"
              key={i}
              active={item.type === currentSettingMenu}
              text={item.name}
              onClick={() => switchType(item.type)}
              addonBefore={<Icon name={item.icon as IconName} size="middle" />}
              badge={item.badge}
            />
          );
        })}
        <SettingModalCommon.SettingDivider className="my-2.5" list-item-inactive="" />
        <NextMenuItem
          staticStyle
          text="退出登录"
          onClick={() => {
            openModal.loading({ title: '正在退出', keyboard: false });
            bizTracker.logout();
            closeSettingModal();
            void logout();
          }}
          addonBefore={<Icon name="MIcSettingsLogout" size="middle" />}
        />
      </div>
      <div className="px-2 text-grey4 text-t3 space-y-2 pb-5">
        <div className="flex flex-col space-y-2">
          <a
            className="underline underline-offset-4"
            target="_blank"
            href={publicLink.userAgreement}
            rel="noreferrer"
          >
            用户协议
          </a>
          <a
            className="underline underline-offset-4"
            target="_blank"
            href={publicLink.privacyPolicy}
            rel="noreferrer"
          >
            隐私条款
          </a>
        </div>
        <VersionFC />
        <ReloadApp />
      </div>
    </div>
  );
});

const ReloadApp: FC = memo(() => {
  const clearUser = useClearUser();

  const handleClick = async () => {
    await clearUser();
    // if (isFlowUsApp.check) {
    //   ipcSendMessage(IpcMessage.ClearCacheAndReload);
    // }
  };

  return (
    <Tooltip
      arrow
      popup="用久了有点卡顿？点我试试"
      className="text-active_color animate-click"
      onClick={handleClick}
    >
      清理缓存重启
    </Tooltip>
  );
});

const VersionFC: FC = memo(() => {
  const openModal = useOpenModal();
  const appVersion = useAppVersion();
  const isUpdateAvailable = useIsUpdateAvailable();
  const isUpdateReload = useIsUpdateReload();
  const [windowsUpdateVersion, setWindowsUpdateVersion] = useState<string | null>(null);

  useEffect(() => {
    if (isWindows) {
      void getWindowsYml().then((res) => {
        if (res) {
          const large = getLargeVersion(res);
          if (getLargeVersion(`${appVersion}`) < large) {
            setWindowsUpdateVersion(res);
          }
        }
      });
    }
  }, [appVersion]);

  const updateApp = useCallback(() => {
    if (isUpdateReload) {
      ipcSendMessage(IpcMessage.UpdateReload);
      openModal.loading({
        className: 'h-auto px-4 py-6',
        autoClose: true,
        keyboard: true,
        title: (
          <div className="text-center">
            <div>正在安装更新</div>
            <div className="text-t3">
              安装过程中可能会自动重启App
              <div className="text-t4 text-grey3 mt-1">
                若更新过程中出现异常，或长时间无反应
                <br />
                <>
                  请点击
                  <a href={publicLink.appDownload} target="_blank" className="text-active_color">
                    下载最新安装包
                  </a>
                  ，覆盖安装即可
                </>
              </div>
            </div>
          </div>
        ),
      });
    } else {
      message.loading('检查更新App');
      ipcSendMessage(IpcMessage.CheckForUpdates);
    }
  }, [isUpdateReload, openModal]);

  const appInfoContent = useMemo(() => {
    if (isWindows) {
      if (windowsUpdateVersion) {
        return (
          <a
            href={publicLink.appDownload}
            target="_blank"
            className="text-active_color animate-click"
          >
            下载新版 {windowsUpdateVersion}
          </a>
        );
      }
      return <div>暂无更新</div>;
    }
    return (
      <div>
        {isUpdateAvailable && (
          <span className="mr-2">{isUpdateReload ? '新版就绪' : '正在下载'}</span>
        )}
        <span onClick={updateApp} className="text-active_color animate-click mt-1">
          {isUpdateReload ? '立即更新' : '检查更新'}
        </span>
      </div>
    );
  }, [isUpdateAvailable, isUpdateReload, updateApp, windowsUpdateVersion]);

  return (
    <>
      <div>版本：{__VERSION__}</div>
      {appVersion && (
        <>
          <div>PC版本：{appVersion}</div>
          {appInfoContent}
        </>
      )}
    </>
  );
});
